import {
  lunchMealToCartId,
  normaliseLunchSettings,
  parseMoneyToCents,
  tt,
  type LunchMeal,
  type LunchSettings,
  type LunchWeek,
} from '@agos/shared'

import type { CartLine } from './cart'

/**
 * Ordering from the daily menu, and the one rule that makes it awkward: **a soup is cheaper when there
 * is a main to go with it.**
 *
 * Legacy carries the discount by juggling two synthetic cart products per soup — a *bundle* line at
 * `soupBundlePrice` and a *full* line at the soup's own price, with `promoteOneSoup` and
 * `demoteOneSoup` moving a unit between them on every add and remove. It works, and it is the kind of
 * imperative pair that drifts: three of the four functions read `useCart.getState()` mid-handler
 * precisely because the hook's value is already stale by then, and a comment says so.
 *
 * Here the split is **derived**. The basket holds one line per meal, identity and quantity only, and
 * how many soup units are bundle-priced is computed from how many mains are in the basket at the moment
 * the total is taken. Nothing to promote, nothing to demote, nothing to get out of step. The order still
 * carries two lines for a partly-bundled soup, because that is what the kitchen and the receipt need to
 * see — but the split is produced at submit time from the same rule the screen showed.
 *
 * The other rules here are legacy's, kept because each is a refusal a customer would otherwise meet at
 * the server: one day per basket, nothing from today after the kitchen stops serving lunch, and nothing
 * at all when the section is switched off.
 */

/** A lunch meal in the basket. `productId` is the negative hash both apps and the server agree on. */
export function lunchLineOf(meal: LunchMeal): Pick<CartLine, 'productId' | 'extras'> {
  return { productId: lunchMealToCartId(meal), extras: '' }
}

export interface LunchBasketMeal {
  meal: LunchMeal
  quantity: number
}

/** The lunch meals in a basket, resolved against a day's menu. */
export function lunchMealsIn(lines: readonly CartLine[], meals: readonly LunchMeal[]): LunchBasketMeal[] {
  const byId = new Map(meals.map((meal) => [lunchMealToCartId(meal), meal]))
  const found: LunchBasketMeal[] = []
  for (const line of lines) {
    const meal = byId.get(line.productId)
    if (meal !== undefined) found.push({ meal, quantity: line.quantity })
  }
  return found
}

/** How many non-soup lunch meals are in the basket — the number of bundle prices available. */
export function mainCount(basket: readonly LunchBasketMeal[]): number {
  return basket
    .filter((entry) => entry.meal.type !== 'soup')
    .reduce((total, entry) => total + entry.quantity, 0)
}

export interface SoupSplit {
  /** Units charged at the bundle price. */
  bundled: number
  /** Units charged at the soup's own price. */
  full: number
}

/**
 * How a soup line splits, given the mains in the basket.
 *
 * Bundle prices are handed out to soups **in basket order** and run out with the mains — two mains and
 * three soups is two bundled and one full. A soup whose own price is already at or below the bundle
 * price is never "bundled": there is no discount to give, and calling it one would print *(s menu)*
 * beside a price that did not change.
 */
export function soupSplit(
  basket: readonly LunchBasketMeal[],
  soup: LunchMeal,
  bundlePriceCents: number | null,
): SoupSplit {
  const quantity = basket.find((entry) => entry.meal.id === soup.id)?.quantity ?? 0
  const ownCents = parseMoneyToCents(soup.price)
  if (bundlePriceCents === null || bundlePriceCents >= ownCents) return { bundled: 0, full: quantity }

  let remaining = mainCount(basket)
  for (const entry of basket) {
    if (entry.meal.type !== 'soup') continue
    if (entry.meal.id === soup.id) break
    // Soups earlier in the basket take their bundle prices first.
    remaining -= Math.min(remaining, entry.quantity)
  }
  const bundled = Math.min(remaining, quantity)
  return { bundled, full: quantity - bundled }
}

/** What one soup costs alongside a main — the meal's own `bundlePrice` if it has one, else the setting. */
export function bundlePriceCentsOf(soup: LunchMeal, settingsBundlePrice: number | null): number | null {
  if (typeof soup.bundlePrice === 'number') return parseMoneyToCents(soup.bundlePrice)
  return settingsBundlePrice === null ? null : parseMoneyToCents(settingsBundlePrice)
}

/** What a lunch line comes to, in cents, with the bundle rule applied. */
export function lunchLineCents(
  basket: readonly LunchBasketMeal[],
  entry: LunchBasketMeal,
  settingsBundlePrice: number | null,
): number {
  const own = parseMoneyToCents(entry.meal.price)
  if (entry.meal.type !== 'soup') return own * entry.quantity
  const bundle = bundlePriceCentsOf(entry.meal, settingsBundlePrice)
  const split = soupSplit(basket, entry.meal, bundle)
  return split.bundled * (bundle ?? own) + split.full * own
}

/** Every lunch line's total, in cents. */
export function lunchSubtotalCents(
  basket: readonly LunchBasketMeal[],
  settingsBundlePrice: number | null,
): number {
  return basket.reduce((total, entry) => total + lunchLineCents(basket, entry, settingsBundlePrice), 0)
}

export interface LunchOrderLine {
  productId: number
  quantity: number
  unitPriceCents: number
  customName: string
  /**
   * The catalogue product this meal is, when the manager has linked one — `FRONTEND.md` §9.39,
   * owner decision option A: **linked for stock, priced by the menu**.
   *
   * It rides in §10A.14's `parts` rather than on the line itself, and that is the whole trick. The
   * line keeps its **negative** id, so `ServerPricing` prices it from `lunch_config` and the menu
   * price is untouched; the part names the product, which is what stock deducts against. Putting the
   * product id on the line would re-price the meal at the à-la-carte price on the customer's bill,
   * which is the reason this waited for a decision at all.
   *
   * Priced at the line's own price, not the catalogue's: a part is an account of what was charged,
   * and a menu price is deliberately not the à-la-carte one.
   */
  parts?: { productId: number; name: string; unitPriceCents: number; quantity: number }[]
}

/** The stock link a linked meal carries, or nothing at all. */
function stockPart(
  meal: LunchMeal,
  name: string,
  unitPriceCents: number,
  quantity: number,
): Pick<LunchOrderLine, 'parts'> {
  return meal.productId === undefined || meal.productId <= 0
    ? {}
    : { parts: [{ productId: meal.productId, name, unitPriceCents, quantity }] }
}

/**
 * The order lines a lunch basket becomes.
 *
 * A partly-bundled soup emits **two** lines, at two prices, with legacy's *(s menu)* suffix on the
 * cheaper one — the server takes `unitPrice` per line for a negative `productId`, and one line at an
 * averaged price would print a figure nobody can check against the menu.
 */
export function lunchOrderLines(
  basket: readonly LunchBasketMeal[],
  settingsBundlePrice: number | null,
): LunchOrderLine[] {
  const lines: LunchOrderLine[] = []
  for (const entry of basket) {
    const id = lunchMealToCartId(entry.meal)
    const own = parseMoneyToCents(entry.meal.price)
    const name = entry.meal.name.trim()
    if (entry.meal.type !== 'soup') {
      if (entry.quantity > 0) {
        lines.push({
          productId: id,
          quantity: entry.quantity,
          unitPriceCents: own,
          customName: name,
          ...stockPart(entry.meal, name, own, entry.quantity),
        })
      }
      continue
    }
    const bundle = bundlePriceCentsOf(entry.meal, settingsBundlePrice)
    const split = soupSplit(basket, entry.meal, bundle)
    if (split.bundled > 0 && bundle !== null) {
      lines.push({
        productId: id,
        quantity: split.bundled,
        unitPriceCents: bundle,
        customName: `${name} ${tt('(s menu)')}`,
        // A partly-bundled soup is two lines, so the stock link is on both — two bowls left the
        // kitchen whichever price they were sold at.
        ...stockPart(entry.meal, name, bundle, split.bundled),
      })
    }
    if (split.full > 0) {
      lines.push({
        productId: id,
        quantity: split.full,
        unitPriceCents: own,
        customName: name,
        ...stockPart(entry.meal, name, own, split.full),
      })
    }
  }
  return lines
}

/** `2026-07-31` from local calendar components — the way lunch days are keyed. */
export function lunchDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Whether today's lunch is past serving.
 *
 * `timeTo` is when the kitchen stops serving lunch, not when the restaurant closes. After it, today's
 * menu cannot be ordered — but another day still can, which is what *predobjednávka* means on the chip.
 */
export function lunchClosedToday(timeTo: string, now: Date): boolean {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeTo.trim())
  if (match === null) return false
  const minutes = Number(match[1]) * 60 + Number(match[2])
  return now.getHours() * 60 + now.getMinutes() >= minutes
}

/** Whether lunch is being served right now, which is what the chip's wording turns on. */
export function lunchServingNow(timeFrom: string, timeTo: string, now: Date): boolean {
  const from = /^(\d{1,2}):(\d{2})$/.exec(timeFrom.trim())
  const to = /^(\d{1,2}):(\d{2})$/.exec(timeTo.trim())
  if (from === null || to === null) return false
  const minutes = now.getHours() * 60 + now.getMinutes()
  return minutes >= Number(from[1]) * 60 + Number(from[2]) && minutes < Number(to[1]) * 60 + Number(to[2])
}

/**
 * Why this meal cannot go in the basket, or `''` when it can.
 *
 * Each of these is a refusal the customer would otherwise meet at the server or, worse, in the kitchen:
 * a lunch order for a day the restaurant has moved past, or a basket carrying two days' menus that
 * cannot both be delivered at once.
 */
export function lunchRefusal(args: {
  visible: boolean
  serving: boolean
  closedToday: boolean
  isToday: boolean
  basketDay: string | null
  day: string
}): string {
  if (!args.visible) return tt('Obedové menu je momentálne vypnuté.')
  if (args.isToday && args.closedToday) {
    return tt('Dnes už je obedové menu zatvorené. Môžete si predobjednať na iné dni.')
  }
  if (args.basketDay !== null && args.basketDay !== args.day) {
    return tt('V košíku môže byť obedové menu iba z jedného dňa.')
  }
  return ''
}

/**
 * `GET /settings/lunch/public` answers `{ weeks, settings }`, and both halves are read here rather
 * than in either module: the menu draws the week and the checkout prices what came out of it, and §5
 * forbids one importing the other.
 */
export function lunchWeeksOf(payload: unknown): LunchWeek[] {
  if (typeof payload !== 'object' || payload === null) return []
  const weeks = (payload as { weeks?: unknown }).weeks
  return Array.isArray(weeks) ? (weeks as LunchWeek[]) : []
}

/** The settings out of the same payload, with legacy's defaults filled in for whatever is missing. */
export function lunchSettingsOf(payload: unknown): LunchSettings {
  const raw =
    typeof payload === 'object' && payload !== null ? (payload as { settings?: unknown }).settings : undefined
  return normaliseLunchSettings(typeof raw === 'object' && raw !== null ? raw : {})
}

/**
 * Every meal in the payload, whichever day or week it is on.
 *
 * The checkout has no day selected — it prices whatever the basket holds — so it resolves against all
 * of them. A meal id is unique across the menu, so there is nothing to disambiguate.
 */
export function allLunchMeals(payload: unknown): LunchMeal[] {
  return lunchWeeksOf(payload).flatMap((week) =>
    week.days.flatMap((day) => (Array.isArray(day.meals) ? day.meals : [])),
  )
}
